/**
 * useLiveOps — heartbeat + event emit hook for non-admin authenticated users.
 *
 * Design choices (per founder spec):
 * - Heartbeat every 60s. Pauses entirely when document.hidden.
 * - Throttle: at most 1 ping/45s even if route changes rapidly (debounced).
 * - Event emit util with dedup: identical (type, label) within 30s is dropped.
 * - Single session_id per page lifetime, kept in sessionStorage so a hard
 *   refresh starts a fresh session but a route change does not.
 * - All HTTP calls are best-effort; failures never bubble.
 */
import { useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useLocation } from "react-router-dom";

const HEARTBEAT_INTERVAL_MS = 60_000;
const MIN_PING_GAP_MS = 45_000;
const EVENT_DEDUP_WINDOW_MS = 30_000;

const SESSION_KEY = "duolyst_live_ops_session";

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (_) {
    return `s_fallback_${Date.now()}`;
  }
}

function detectPlatform() {
  const ua = navigator.userAgent || "";
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  return "web";
}

function detectDevice() {
  const ua = navigator.userAgent || "";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  return "Desktop";
}

// ── singleton emit util (importable directly, doesn't need the hook) ──
const _eventCache = new Map(); // key → lastSentMs
let _isAdminUser = false;

function _normaliseRoute(pathname) {
  return (pathname || "/").replace(/\/[a-f0-9-]{8,}/gi, "/:id").slice(0, 120);
}

function _shouldDedup(key) {
  const now = Date.now();
  const last = _eventCache.get(key) || 0;
  if (now - last < EVENT_DEDUP_WINDOW_MS) return true;
  _eventCache.set(key, now);
  // Keep cache compact
  if (_eventCache.size > 200) {
    const cutoff = now - EVENT_DEDUP_WINDOW_MS;
    for (const [k, v] of _eventCache.entries()) {
      if (v < cutoff) _eventCache.delete(k);
    }
  }
  return false;
}

export async function emitLiveEvent(type, options = {}) {
  if (_isAdminUser) return;
  const { label, importance = "normal", metadata } = options;
  const dedupKey = `${type}:${label || ""}`;
  if (_shouldDedup(dedupKey)) return;
  const session_id = getSessionId();
  try {
    await axios.post(`${API}/live/event`, {
      type,
      label,
      importance,
      metadata,
      session_id,
    });
  } catch (_) { /* best-effort */ }
}

export async function emitLiveError(category, error, extra = {}) {
  if (_isAdminUser) return;
  try {
    await axios.post(`${API}/live/error`, {
      category,
      message: (error?.message || String(error || "Unknown error")).slice(0, 500),
      stack: (error?.stack || "").slice(0, 4000),
      route: _normaliseRoute(window.location?.pathname || ""),
      component: extra.component,
      extra,
      session_id: getSessionId(),
    });
  } catch (_) { /* swallow */ }
}

// ── hook ──

export function useLiveOps() {
  const { user, token } = useAuth();
  const location = useLocation();
  const lastPingRef = useRef(0);
  const intervalRef = useRef(null);

  // Detect admin once and skip everything for them
  useEffect(() => {
    _isAdminUser = !!(user && (user.is_admin === true ||
      (user.email && (user.email.toLowerCase() === "ohaughton2@gmail.com" ||
                       user.email.toLowerCase() === "admin@duolyst.com"))));
  }, [user]);

  const sendPing = useCallback(async (force = false) => {
    if (!token || _isAdminUser) return;
    const now = Date.now();
    if (!force && now - lastPingRef.current < MIN_PING_GAP_MS) return;
    lastPingRef.current = now;
    try {
      const r = await axios.post(`${API}/live/ping`, {
        session_id: getSessionId(),
        current_route: _normaliseRoute(location.pathname),
        current_feature: window.__duolyst_current_feature || null,
        active_modal: window.__duolyst_active_modal || null,
        last_action: window.__duolyst_last_action || null,
        device: detectDevice(),
        platform: detectPlatform(),
      });
      // Deploy-SHA mismatch detector: pings expose the server's running SHA.
      // First ping captures it; subsequent diffs surface a "new version" toast
      // exactly once per session.
      const sha = r?.data?.deploy_sha;
      if (sha) {
        const known = window.__duolyst_deploy_sha;
        if (!known) {
          window.__duolyst_deploy_sha = sha;
        } else if (known !== sha && !window.__duolyst_deploy_sha_warned) {
          window.__duolyst_deploy_sha_warned = true;
          try {
            const { toast } = await import("sonner");
            toast.message("✨ A fresh version is available", {
              description: "Tap to reload and get the latest features.",
              duration: 12000,
              action: {
                label: "Reload",
                onClick: () => window.location.reload(),
              },
            });
          } catch (_) {}
        }
      }
    } catch (_) { /* best-effort */ }
  }, [token, location.pathname]);

  // Heartbeat loop with Page Visibility API gating
  useEffect(() => {
    if (!token || _isAdminUser) return;
    sendPing(true);

    const start = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => sendPing(false), HEARTBEAT_INTERVAL_MS);
    };
    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        sendPing(true); // immediate ping on regain focus
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [token, sendPing]);

  // Re-ping on route change (debounced via MIN_PING_GAP_MS)
  useEffect(() => {
    sendPing(false);
  }, [location.pathname, sendPing]);

  return { emitLiveEvent, emitLiveError };
}

export default useLiveOps;
